import { Injectable } from '@angular/core';
import { asn1, md, pki, pkcs12, util } from 'node-forge';
import * as XmlDSigJs from 'xmldsigjs';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { SigningParams } from './signing-params';
import { SignFileInfo } from './sign-file-info';
import { FileItemInfo } from './file-item-info';
const forge = require('node-forge');

@Injectable()
export class DocumentSigningService {
  private readonly signatureFileName = 'Naglowek.xml';
  private readonly defaultFileType = 'DokumentZawiadomienia';
  private readonly pdfFileType = 'Wypis';
  private readonly certificateStorageKey = 'certyfikatZsin';
  private readonly certificatePasswordKey = 'certyfikatInfo';
  private readonly doneState = 2;
  private readonly keyHash = 'SHA-1';
  private readonly algorithm = {
    name: 'RSASSA-PKCS1-v1_5',
    hash: this.keyHash,
    modulusLength: 1024,
    publicExponent: new Uint8Array([1, 0, 1])
  };
  private isCertificateStored: BehaviorSubject<boolean>;

  constructor() {
    const isStored = this.restoreCertificate() !== null;
    this.isCertificateStored = new BehaviorSubject<boolean>(isStored);
  }

  get isCertificateStoredValue(): boolean {
    return this.isCertificateStored.getValue();
  }

  get isCertificateStoredStream(): Observable<boolean> {
    return this.isCertificateStored.asObservable();
  }

  verifyCertificate(certificate: File, password: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.readCertificateFromFile(certificate).then(content => {
        let isValid = false;
        try {
          const key = this.extractPrivateKey(content, password);
          isValid = typeof key.sign === 'function';
        } catch {}
        resolve(isValid);
      });
    });
  }

  storeCertificate(certificate: File): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      this.readCertificateFromFile(certificate).then(content => {
        localStorage.setItem(this.certificateStorageKey, content);
        this.isCertificateStored.next(true);
        resolve(true);
      });
    });
  }

  restoreCertificate(): string {
    return localStorage.getItem(this.certificateStorageKey);
  }

  storeCertPassword(password: string) {
    localStorage.setItem(this.certificatePasswordKey, password);
  }

  clearCertificate() {
    localStorage.removeItem(this.certificateStorageKey);
    this.isCertificateStored.next(false);
  }

  signFiles(files: File[], signFileInfo: SignFileInfo, password: string): Promise<File> {
    return new Promise<File>(resolve => {
      this.extractKeys(this.restoreCertificate(), password).then(keys => {
        this.getSignatureContent(files, signFileInfo).then(unsignedContent => {
          this.signXml(unsignedContent, keys).then(signedContent => {
            resolve(this.createFile(this.signatureFileName, signedContent));
          });
        });
      });
    });
  }

  private restoreCertPassword(): string {
    return localStorage.getItem(this.certificatePasswordKey);
  }

  private readCertificateFromFile(certificate: File): Promise<string> {
    return new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.readyState === this.doneState) {
          const content = btoa(reader.result.toString());
          resolve(content);
        }
      };
      reader.readAsBinaryString(certificate);
    });
  }

  private createFile(fileName: string, content: string): File {
    const contentType = 'application/xml';
    const blob = new Blob([content], { type: contentType });
    return new File([blob], fileName, { type: contentType, lastModified: Date.now() });
  }

  private getSignatureFileName(fileName: string): string {
    return fileName.replace(/\.[^\.]+$/, '_metryka.xml');
  }

  private getSignatureContent(files: File[], signFileInfo: SignFileInfo): Promise<string> {
    return new Promise<string>(resolve => {
      this.getAllFileItems(files).then(items => {
        resolve(this.buildSignatureContent(signFileInfo, items));
      });
    });
  }

  private getAllFileItems(files: File[]): Promise<string[]> {
    const promises = [];
    for (const file of files) {
      promises.push(
        new Promise<string>(resolve => {
          this.getFileItem(file).then(item => {
            resolve(item);
          });
        })
      );
    }

    return Promise.all(promises);
  }

  private getFileItem(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async event => {
        const arrayBuffer = reader.result;
        try {
          const item = await this.computeAndFulfillFileDigest(file.name, arrayBuffer as ArrayBuffer);
          resolve(this.substituteTags(SigningParams.fileItemTemplate, item));
        } catch (error) {
          reject(new Error('Error computing hash'));
        }
      };
      reader.readAsArrayBuffer(file);
    });
  }

  private buildSignatureContent(signFileInfo: SignFileInfo, items: string[]) {
    const values = {
      changeIdentifier: signFileInfo.changeIdentifier,
      sequenceNumber: signFileInfo.sequenceNumber,
      fileItems: items.join('')
    };

    return this.substituteTags(SigningParams.signFileTemplate, values);
  }

  private substituteTags(template: string, values: any): string {
    let content = template;

    for (const name in values) {
      if (values.hasOwnProperty(name)) {
        content = content.replace('{' + name + '}', values[name]);
      }
    }

    return content;
  }

  private async computeAndFulfillFileDigest(fileName: string, content: ArrayBuffer): Promise<FileItemInfo> {
    const buffer = await crypto.subtle.digest('SHA-256', content);
    const hashArray = Array.from(new Uint8Array(buffer));
    const hashHex = hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
    return {
      fileName: fileName,
      fileType: this.getFileType(fileName),
      digestHex: hashHex.toUpperCase()
    };
  }

  private getFileType(fileName: string): string {
    let ext = fileName.split('.').pop();
    if (!ext || ext.toLowerCase() != 'pdf')
      return this.defaultFileType;
    return this.pdfFileType;
  }

  private signXml(xml: string, keys: CryptoKeyPair): Promise<string> {
    const options: XmlDSigJs.OptionsSign = {
      keyValue: keys.publicKey,
      references: [{ hash: this.keyHash, transforms: ['enveloped', 'c14n'], uri: "" }]
    };

    return new Promise<string>(resolve => {
      const signature = new XmlDSigJs.SignedXml();
      signature.Sign(this.algorithm, keys.privateKey, XmlDSigJs.Parse(xml), options).then(() => {
        resolve(signature.toString());
      });
    });
  }

  private extractKeys(certificate: string, password: string): Promise<CryptoKeyPair> {
    const binary = atob(certificate);
    const p12Asn1 = asn1.fromDer(binary);
    const p12 = pkcs12.pkcs12FromAsn1(p12Asn1, false, password);
    let privateKey;
    let cert;

    for (let sci = 0; sci < p12.safeContents.length; sci++) {
      const safeContents = p12.safeContents[sci];
      for (let sbi = 0; sbi < safeContents.safeBags.length; sbi++) {
        const safeBag = safeContents.safeBags[sbi];
        if (safeBag.type === pki.oids.keyBag) {
          privateKey = safeBag.key;
        } else if (safeBag.type === pki.oids.pkcs8ShroudedKeyBag) {
          privateKey = safeBag.key;
        } else if (safeBag.type === pki.oids.certBag) {
          cert = safeBag.cert;
        }
      }
    }

    return new Promise<CryptoKeyPair>(resolve => {
      const keys = {
        privateKey: null,
        publicKey: null
      };
      this.importCryptoKeyPkcs8(this.privateKeyToPkcs8(privateKey), true).then(key => {
        keys.privateKey = key;
        resolve(keys);
      });
    });
  }

  private extractCert(certificate: string, password: string) {
    const p12 = this.convertToP12(certificate, password);
    const bags = p12.getBags({ bagType: pki.oids.certBag });
    const certBag = bags[pki.oids.certBag][0];

    return certBag.cert;
  }

  private extractPrivateKey(certificate: string, password: string) {
    const p12 = this.convertToP12(certificate, password);
    const bags = p12.getBags({ bagType: pki.oids.pkcs8ShroudedKeyBag });
    const keyBag = bags[pki.oids.pkcs8ShroudedKeyBag][0];

    return keyBag.key;
  }

  private convertToP12(certificate: string, password: string): pkcs12.Pkcs12Pfx {
    const p12Der = util.decode64(certificate);
    const p12Asn1 = asn1.fromDer(p12Der);
    return pkcs12.pkcs12FromAsn1(p12Asn1, false, password);
  }

  private privateKeyToPkcs8(privateKey: string): ArrayBuffer {
    const rsaPrivateKey = forge.pki.privateKeyToAsn1(privateKey);
    const privateKeyInfo = forge.pki.wrapRsaPrivateKey(rsaPrivateKey);
    const privateKeyInfoDer = forge.asn1.toDer(privateKeyInfo).getBytes();
    const privateKeyInfoDerBuff = this.stringToArrayBuffer(privateKeyInfoDer);

    return privateKeyInfoDerBuff;
  }

  private publicKeyToPkcs8(publicKey: string): ArrayBuffer {
    const subjectPublicKeyInfo = forge.pki.publicKeyToAsn1(publicKey);
    const der = forge.asn1.toDer(subjectPublicKeyInfo).getBytes();

    return this.stringToArrayBuffer(der);
  }

  private importCryptoKeyPkcs8(key: ArrayBuffer, extractable: boolean): PromiseLike<CryptoKey> {
    return crypto.subtle.importKey(
      'pkcs8',
      key,
      { name: 'RSASSA-PKCS1-v1_5', hash: { name: this.keyHash } },
      extractable,
      ['sign']
    );
  }

  private stringToArrayBuffer(data) {
    const arrBuff = new ArrayBuffer(data.length);
    const writer = new Uint8Array(arrBuff);
    for (let index = 0, len = data.length; index < len; index++) {
      writer[index] = data.charCodeAt(index);
    }

    return arrBuff;
  }

  private arrayBufferToString(buffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let index = 0; index < len; index++) {
      binary += String.fromCharCode(bytes[index]);
    }

    return binary;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/document-signing/document-signing.service.ts